import React, { useEffect, useState } from "react";
import ApexCharts from "apexcharts";
import { QuestionDetails } from "./QuestionCard";

const AnalysisChart: React.FC<{
    QuestionDetails: QuestionDetails;
    Accuracy: number;
    Type: string;
}> = ({ QuestionDetails, Accuracy, Type }) => {
    const [isDarkMode, setIsDarkMode] = useState(
        Boolean(localStorage.getItem("darkTheme"))
    );
    const [totalResponded, setTotalResponded] = useState(0);

    useEffect(() => {
        const totalResponded =
            QuestionDetails.answeredCorrectly +
            QuestionDetails.answeredIncorrectly +
            QuestionDetails.unanswered;
        setTotalResponded(totalResponded);
    }, [QuestionDetails]);
    const safeDivide = (numerator: number, denominator: number) =>
        denominator === 0 ? 0 : parseFloat(((numerator * 100) / denominator).toFixed(2));

    const getChartOptions = (): any => ({
        series: [
            safeDivide(QuestionDetails.answeredCorrectly, totalResponded),
            safeDivide(QuestionDetails.answeredIncorrectly, totalResponded),
            safeDivide(QuestionDetails.unanswered, totalResponded),
        ],

        colors: ["#4CAF50", "#F44336", "#FFC107"],
        chart: {
            height: 250,
            width: "100%",
            type: "donut",
        },
        stroke: {
            colors: ["transparent"],
        },
        plotOptions: {
            pie: {
                donut: {
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontFamily: "Inter, sans-serif",
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: isDarkMode ? "#FFFFFF" : "#333333", // Adjust name color
                            offsetY: 20,
                        },
                        total: {
                            showAlways: true,
                            show: true,
                            label: "Total Candidates",
                            fontFamily: "Inter, sans-serif",
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: isDarkMode ? "#FFFFFF" : "#333333",
                            formatter: () => {
                                return `${totalResponded}`;
                            },
                        },
                        value: {
                            show: true,
                            fontFamily: "Inter, sans-serif",
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: isDarkMode ? "#FFFFFF" : "#000000",
                            offsetY: -20,
                            formatter: (value: any) => `${value}`,
                        },
                    },
                    size: "80%",
                },
            },
        },
        labels: ["Correct", "Incorrect", "Not Responded"], // Labels for the chart
        dataLabels: {
            enabled: false,
        },
        legend: {
            position: "bottom",
            fontFamily: "Inter, sans-serif",
            fontSize: "12px", // Font size for the legend
            fontWeight: "bold",
            labels: {
                colors: [isDarkMode ? "#FFFFFF" : "#333333"], // Adjust legend text color
            },
            formatter: (label: string) => {
                return `<span style="color: ${isDarkMode ? "#FFFFFF" : "#333333"
                    }; margin-left: 3px;">${label}</span>`;
            },
        },
    });

    useEffect(() => {
        const defaultThemeIsDark = Boolean(localStorage.getItem("darkTheme"));
        setIsDarkMode(defaultThemeIsDark);
    }, []);

    useEffect(() => {
        const chartElement = document.getElementById("donut-chart");
        let chart: ApexCharts | null = null;

        if (chartElement && typeof ApexCharts !== "undefined") {
            chart = new ApexCharts(chartElement, getChartOptions());
            chart.render();
        }

        return () => {
            if (chart) {
                chart.destroy();
            }
        };
    }, [totalResponded, isDarkMode, QuestionDetails, getChartOptions]);

    return (
        <div className="w-full bg-white dark:bg-darkbglight py-3 px-6">
            <div className="flex justify-between mt-3">
                <div className="flex justify-center items-center">
                    <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white pe-1">
                        Analysis Chart
                    </h5>
                </div>
            </div>

            <div className="flex items-center justify-around space-x-4">
                <div
                    className="py-6 text-gray-600 dark:text-gray-100"
                    id="donut-chart"
                ></div>
                <div className="h-[250px] flex flex-col items-center justify-center space-y-5">
                    <span
                        className={`h-[200px] w-[200px] self-center justify-self-center flex items-center justify-center dark:text-white text-gray-600 rounded-full border-[16px] 
                            ${Type === "audio" ||
                                Type === "text" ||
                                Type === "video" ||
                                (((QuestionDetails.answeredIncorrectly + QuestionDetails.unanswered) / (QuestionDetails.unanswered + QuestionDetails.answeredIncorrectly + QuestionDetails.answeredCorrectly)) * 100) === -1
                                ? "border-gray-400"
                                : (((QuestionDetails.answeredIncorrectly + QuestionDetails.unanswered) / (QuestionDetails.unanswered + QuestionDetails.answeredIncorrectly + QuestionDetails.answeredCorrectly)) * 100) <= 40
                                    ? "border-green-600"
                                    : (((QuestionDetails.answeredIncorrectly + QuestionDetails.unanswered) / (QuestionDetails.unanswered + QuestionDetails.answeredIncorrectly + QuestionDetails.answeredCorrectly)) * 100) <= 75
                                        ? "border-orange-300"
                                        : "border-red-500"
                            } `}
                    >
                        <p className="text-2xl">
                            {(() => {
                                if (Type === "audio" || Type === "text" || Type === "video") {
                                    return <small>N/A</small>;
                                }
                                const total =
                                    QuestionDetails.answeredIncorrectly +
                                    QuestionDetails.unanswered +
                                    QuestionDetails.answeredCorrectly;

                                const difficulty =
                                    ((QuestionDetails.answeredIncorrectly + QuestionDetails.unanswered) /
                                        total) *
                                    100;

                                return isNaN(difficulty) ? (
                                    <>{QuestionDetails.accuracy} %</>
                                ) : (
                                    difficulty.toFixed(2) + "%"
                                );
                            })()}
                        </p>
                    </span>
                    <div className="font-bold tracking-wider text-sm">
                        Difficulty
                    </div>
                </div>
            </div>
            <div className="mt-3 text-xs flex flex-row items-center gap-3 -mx-2">
                <p className="dark:bg-gray-600 bg-gray-200 px-3 py-1.5 rounded-md dark:text-white">
                    <strong>Answered Correctly:</strong> {QuestionDetails.answeredCorrectly}
                </p>
                <p className="dark:bg-gray-600 bg-gray-200 px-3 py-1.5 rounded-md dark:text-white">
                    <strong>Answered Incorrectly:</strong> {QuestionDetails.answeredIncorrectly}
                </p>
                <p className="dark:bg-gray-600 bg-gray-200 px-3 py-1.5 rounded-md dark:text-white">
                    <strong>Unanswered:</strong> {QuestionDetails.unanswered}
                </p>
            </div>
        </div>
    );
};

export default AnalysisChart;
